import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { BuildingInsights, RoofSegmentSizeAndSunshineStats } from '../../../../models/maps/building-insights';
import { GoogleMapsService } from '../../../../../services/google-maps.service';
import { ToastService } from '../../../../../services/toast.service';
import { LoaderService } from '../../../../../services/loader.service';
import { finalize } from 'rxjs';
import { inOutAnimation } from '../../../../animations';
import { CalculatorColorConfig, defaultCalculatorColorConfig } from '../simple-calculator/calculator-color-config';

@Component({
  selector: 'psc-simple-calculator-map',
  templateUrl: './simple-calculator-map.component.html',
  styleUrls: ['./simple-calculator-map.component.scss'],
  animations: [
    inOutAnimation({}),
  ],
})
export class SimpleCalculatorMapComponent implements OnInit {

  @Input() orientation: number;

  @Input() set dataInputDone(value: boolean) {
    this._dataInputDone = value;
    const newGestureMode = value ? 'none' : 'auto';
    this.mapOptions = {
      ...this.mapOptions,
      gestureHandling: newGestureMode,
    };
  }

  @Input() skipSolarApi: boolean = false;

  @Output() locationSet: EventEmitter<google.maps.LatLngLiteral> = new EventEmitter<google.maps.LatLngLiteral>();
  @Output() roofSegmentSelected: EventEmitter<RoofSegmentSizeAndSunshineStats> = new EventEmitter<RoofSegmentSizeAndSunshineStats>();
  @Output() solarApiInfoNotFound: EventEmitter<void> = new EventEmitter<void>();

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerWidth = window.innerWidth;
  }

  innerWidth: number;
  _dataInputDone: boolean;
  center: google.maps.LatLngLiteral = { lat: 45.8, lng: 15.97 };
  markerPosition: google.maps.LatLngLiteral;
  mapZoom = 13;
  mapOptions: google.maps.MapOptions = {
    tilt: 0,
    rotateControl: false,
    mapTypeId: google.maps.MapTypeId.HYBRID,
  };

  solarApiMarkers: google.maps.MarkerOptions[];
  buildingInsights?: BuildingInsights;
  selectedMarker: google.maps.MarkerOptions;
  selectedRoofSegment: RoofSegmentSizeAndSunshineStats | null;
  showSolarApiOption: boolean;

  mapState: SimpleGoogleMapState = SimpleGoogleMapState.Initial;
  roofSegmentConfirmed: boolean;
  addressValue: string;
  locationNotSelectedError: boolean;

  // Drawing
  private mapInstance: google.maps.Map;
  private drawnPolygon: google.maps.Polygon | null = null;
  drawnAreaM2: number | null = null;
  isDrawingPolygon = false;

  constructor(
    private googleMapsService: GoogleMapsService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
  }

  mapInitialized(map: google.maps.Map) {
    this.mapInstance = map;
  }

  onMapClick(latLng: google.maps.LatLngLiteral | undefined) {
    if (this.isDrawingPolygon) {
      this.addDrawingPoint(latLng);
      return;
    }
    this.setLocationAndFillAddress(latLng);
  }

  getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          if (position) {
            const userLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
            this.setLocationAndFillAddress(userLocation);
            this.center = userLocation;
          }
        });
    }
  }

  setLocation(location: google.maps.LatLngLiteral | undefined) {
    if (!location || this.mapState !== SimpleGoogleMapState.Initial || this._dataInputDone) {
      return;
    }
    this.locationNotSelectedError = false;
    this.markerPosition = location;
    this.locationSet.emit(this.markerPosition);
  }

  setLocationAndFillAddress(location: google.maps.LatLngLiteral | undefined) {
    if (!location || this.mapState !== SimpleGoogleMapState.Initial || this._dataInputDone) {
      return;
    }

    this.googleMapsService.getAddressFromLatLngPromise(location).subscribe(
      (res) => {
        if (res?.length > 0) {
          this.addressValue = res[0].formatted_address;
        }
      });
    this.setLocation(location);
  }

  private setLocationFromDrawnPolygon(location: google.maps.LatLngLiteral | undefined, fillAddress: boolean) {
    if (!location || this._dataInputDone) {
      return;
    }
    this.locationNotSelectedError = false;
    this.markerPosition = location;
    this.locationSet.emit(this.markerPosition);
    if (fillAddress) {
      this.googleMapsService.getAddressFromLatLngPromise(location).subscribe(
        (res) => {
          if (res?.length > 0) {
            this.addressValue = res[0].formatted_address;
          }
        });
    }
  }

  getSolarData(location: google.maps.LatLngLiteral) {
    this.loaderService.showLoader();
    this.googleMapsService.getSolarBuildingInsights(location)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe(
        {
          next: (buildingInsights) => {
            this.buildingInsights = buildingInsights as BuildingInsights;
            this.solarApiMarkers = buildingInsights.solarPotential.roofSegmentStats.map((stat) => this.roofSegmentToMarker(stat));
            this.setLocation({
              lng: this.buildingInsights.center.longitude,
              lat: this.buildingInsights.center.latitude,
            });
            this.center = this.markerPosition;
            this.mapState = SimpleGoogleMapState.SolarApiFound;
            this.showSolarApiOption = true;
          },
          error: err => {
            this.showSolarApiOption = false;
            this.mapState = SimpleGoogleMapState.SolarApiNotFound;
            this.solarApiInfoNotFound.emit();
          },
        });
  }

  private roofSegmentToMarker(stat: RoofSegmentSizeAndSunshineStats) {
    const marker: google.maps.MarkerOptions = {
      position: { lat: stat.center.latitude, lng: stat.center.longitude },
      icon: this.getRoofSegmentIcon(),
    };
    return marker;
  }

  confirmLocation() {
    if (!this.markerPosition) {
      this.locationNotSelectedError = true;
      return;
    }
    this.locationNotSelectedError = false;
    this.center = this.markerPosition;
    this.mapZoom = 21;
    if (this.skipSolarApi) {
      this.solarApiInfoNotFound.emit();
      this.mapState = SimpleGoogleMapState.DrawingRoof;
      this.startPolygonDrawing();
      return;
    }
    this.getSolarData(this.markerPosition);
  }

  /** Starts manual click-to-add-vertex drawing (google.maps.drawing.DrawingManager was removed from the Maps JS API). */
  private startPolygonDrawing() {
    if (!this.mapInstance) return;

    if (this.drawnPolygon) {
      this.drawnPolygon.setMap(null);
      this.drawnPolygon = null;
    }
    this.drawnAreaM2 = null;
    this.isDrawingPolygon = true;

    const primaryColor = this.colorConfig.primaryColor || '#1B4F8A';
    this.drawnPolygon = new google.maps.Polygon({
      map: this.mapInstance,
      strokeColor: primaryColor,
      fillColor: primaryColor,
      fillOpacity: 0.35,
      strokeWeight: 2,
      editable: false,
    });
  }

  addDrawingPoint(latLng: google.maps.LatLngLiteral | undefined) {
    if (!this.isDrawingPolygon || !latLng || !this.drawnPolygon) return;
    this.drawnPolygon.getPath().push(new google.maps.LatLng(latLng));
    this.changeDetectorRef.detectChanges();
  }

  get canFinishDrawing(): boolean {
    return this.isDrawingPolygon && !!this.drawnPolygon && this.drawnPolygon.getPath().getLength() >= 3;
  }

  finishDrawing() {
    if (!this.canFinishDrawing || !this.drawnPolygon) return;

    this.isDrawingPolygon = false;
    const polygon = this.drawnPolygon;
    polygon.setOptions({ editable: true });
    this.drawnAreaM2 = google.maps.geometry.spherical.computeArea(polygon.getPath());
    this.mapState = SimpleGoogleMapState.RoofDrawn;

    this.setLocationFromDrawnPolygon(this.computeCentroid(polygon.getPath().getArray()), true);

    google.maps.event.addListener(polygon.getPath(), 'set_at', () => this.recalculateArea());
    google.maps.event.addListener(polygon.getPath(), 'insert_at', () => this.recalculateArea());

    this.emitDrawnRoofSegment();
    this.changeDetectorRef.detectChanges();
  }

  private recalculateArea() {
    if (!this.drawnPolygon) return;
    this.drawnAreaM2 = google.maps.geometry.spherical.computeArea(this.drawnPolygon.getPath());
    this.setLocationFromDrawnPolygon(this.computeCentroid(this.drawnPolygon.getPath().getArray()), false);
    this.emitDrawnRoofSegment();
    this.changeDetectorRef.detectChanges();
  }

  private emitDrawnRoofSegment() {
    if (!this.drawnPolygon || this.drawnAreaM2 === null) return;
    const centroid = this.computeCentroid(this.drawnPolygon.getPath().getArray());
    const segment: RoofSegmentSizeAndSunshineStats = {
      stats: {
        areaMeters2: Math.round(this.drawnAreaM2),
        sunshineQuantiles: [],
        groundAreaMeters2: Math.round(this.drawnAreaM2),
      },
      center: { latitude: centroid.lat, longitude: centroid.lng },
      boundingBox: { sw: { latitude: centroid.lat, longitude: centroid.lng }, ne: { latitude: centroid.lat, longitude: centroid.lng } },
      pitchDegrees: 0,
      azimuthDegrees: 180,
      planeHeightAtCenterMeters: 0,
    };
    this.roofSegmentSelected.emit(segment);
  }

  resetDrawing() {
    this.mapState = SimpleGoogleMapState.DrawingRoof;
    this.startPolygonDrawing();
  }

  private computeCentroid(points: google.maps.LatLng[]): google.maps.LatLngLiteral {
    let lat = 0;
    let lng = 0;
    for (const point of points) {
      lat += point.lat();
      lng += point.lng();
    }
    return { lat: lat / points.length, lng: lng / points.length };
  }

  solarApiMarkerClicked(marker: google.maps.MarkerOptions, index: number) {
    this.selectedRoofSegment = (this.buildingInsights as BuildingInsights).solarPotential.roofSegmentStats[index];
    if (this.selectedMarker) {
      this.selectedMarker.icon = this.getRoofSegmentIcon();
    }
    this.selectedMarker = marker;
    marker.icon = this.getRoofSegmentIcon(35);
    this.roofSegmentConfirmed = false;
  }

  getRoofSegmentIcon(size: number = 30) {
    return {
      url: "assets/images/roof_icon.png",
      scaledSize: new google.maps.Size(size, size),
    };
  }

  selectCurrentSegment() {
    this.roofSegmentSelected.emit(this.selectedRoofSegment as RoofSegmentSizeAndSunshineStats);
    this.roofSegmentConfirmed = true;
  }

  protected readonly GoogleMapState = SimpleGoogleMapState;
  @Input() colorConfig: CalculatorColorConfig = defaultCalculatorColorConfig;

  closeSegment() {
    this.selectedRoofSegment = null;
    this.selectedMarker.icon = this.getRoofSegmentIcon();
  }

  stopSegmentSelection() {
    this.buildingInsights = undefined;
    this.solarApiMarkers = [];
  }

  startFromBeginning() {
    if (this.drawnPolygon) {
      this.drawnPolygon.setMap(null);
      this.drawnPolygon = null;
    }
    this.drawnAreaM2 = null;
    this.isDrawingPolygon = false;
    this.stopSegmentSelection();
    this.mapState = SimpleGoogleMapState.Initial;
    this.showSolarApiOption = true;
  }

  cancelSolarDataSelect() {
    this.stopSegmentSelection();
    this.mapState = SimpleGoogleMapState.Initial;
  }

  setAddress(location: google.maps.places.PlaceResult) {
    this.setLocation(location.geometry?.location?.toJSON());
    this.center = this.markerPosition;
    this.mapZoom = 21;
    this.changeDetectorRef.detectChanges();
  }
}


export enum SimpleGoogleMapState {
  Initial,
  SolarApiFound,
  SolarApiNotFound,
  DrawingRoof,
  RoofDrawn,
}
